import { forwardRef, useImperativeHandle, useRef } from 'react';
import type { Currency, Document, PrintableRef } from '../types';
import { useBrand } from '@/lib/brand';

// Helper functions for tax calculations
const hasVatTax = (taxes: any[]) => {
  console.log("taxes", taxes);
  return taxes?.some(tax => 
    tax.taxName?.toLowerCase().includes('vat') || 
    tax.taxName?.toLowerCase().includes('value added tax')
  );
};

const calculateItemVat = (item: any, taxes: any[], isInclusive: boolean) => {
  const vatTax = taxes?.find(tax => 
    tax.taxName?.toLowerCase().includes('vat') || 
    tax.taxName?.toLowerCase().includes('value added tax')
  );
  
  if (!vatTax) return null;
  
  const itemTotal = item.quantity * item.unitPrice;
  
  if (isInclusive) {
    // If tax is inclusive, the total already includes VAT
    const vatRate = vatTax.taxRate / 100;
    const divisor = 1 + vatRate;
    const totalExclVat = itemTotal / divisor;
    const vatAmount = itemTotal - totalExclVat;
    
    return {
      totalExclVat,
      vatAmount,
      totalInclVat: itemTotal
    };
  } else {
    // If tax is exclusive, calculate VAT on top of the total
    const vatAmount = (itemTotal * vatTax.taxRate) / 100;
    
    return {
      totalExclVat: itemTotal,
      vatAmount,
      totalInclVat: itemTotal + vatAmount
    };
  }
};

interface PrintableDocumentProps {
  document: any;
  linkedInvoice?: Document | null;
}

const PrintableDocument = forwardRef<PrintableRef, PrintableDocumentProps>(
  ({ document }, ref) => {
    const componentRef = useRef<HTMLDivElement>(null);
    const brand = useBrand();

    useImperativeHandle(ref, () => ({
      print: () => {
        const printContent = componentRef.current?.innerHTML || '';
        const printWindow = window.open('', '_blank');
        if (printWindow) {
          printWindow.document.write(`
            <!DOCTYPE html>
            <html>
              <head>
                <title>${document.type === 'receipt' ? 'Receipt #' : document.type === 'invoice' ? 'Invoice #' : document.type === 'bill' ?  'Bill #' : 'Quotation #'}${document.number}</title>
                <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@400;600&display=swap" rel="stylesheet">
                <style>
                  body { font-family: 'Poppins', sans-serif; }
                  .print-container { max-width: 800px; margin: 40px auto; padding: 20px; border: 1px solid #e0e0e0; border-radius: 8px; }
                  .header, .footer { text-align: center; margin-bottom: 20px; }
                  .header img { max-width: 150px; }
                  .details { display: flex; justify-content: space-between; margin-bottom: 20px; }
                  .details div { width: 48%; }
                  .details h2 { margin-bottom: 10px; font-size: 1em; }
                  table { width: 100%; border-collapse: collapse; margin: 20px 0; font-size: 0.8em; }
                  th, td { padding: 10px; text-align: left; border-bottom: 1px solid #ddd; }
                  th { background-color: #f5f5f5; }
                  p { margin: 0; font-size: 0.8em; }
                  .total { font-weight: bold; text-align: right; font-size: 0.8em; }
                  .watermark {
                    position: absolute;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%) rotate(-45deg);
                    font-size: 4em;
                    opacity: 0.1;
                    pointer-events: none;
                  }
                  @media print {
                    body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
                  }
                </style>
              </head>
              <body>
                <div class="print-container">
                  ${printContent}
                </div>
              </body>
            </html>
          `);
          printWindow.document.close();

          const images = printWindow.document.images;
          let loadedImagesCount = 0;

          const checkAllImagesLoaded = () => {
            loadedImagesCount++;
            if (loadedImagesCount === images.length) {
              printWindow.print();
            }
          };

          if (images.length === 0) {
            printWindow.print();
          } else {
            for (let i = 0; i < images.length; i++) {
              images[i].onload = checkAllImagesLoaded;
              images[i].onerror = checkAllImagesLoaded;
            }
          }
        }
      },
    }));

    const currencySymbols: Record<Document['currency'], string> = {
      USD: '$',
      ZWG: 'ZWG',
      ZAR: 'R',
      MZN: 'MZN',
      GBP: '£',
      EUR: '€',
      CAD: 'CA$',
      AUD: 'A$',
      CHF: 'CHF',
      CNY: '¥',
      HKD: 'HK$',
      INR: '₹',
      JPY: '¥',
      MXN: 'MX$',
      NZD: 'NZ$',
      RUB: '₽',
      SAR: 'SAR',
      SGD: 'S$',
      THB: '฿',
      TRY: '₺',
      UAH: '₴',
      VND: '₫',
      ZMW: 'ZMW'
    };

    const subtotal = document.items ? document.items.reduce(
      (sum: number, item: any) => sum + item.quantity * item.unitPrice,
      0
    ) : 0;
    const total = subtotal + (document.taxes?.reduce((sum: number, tax: any) => sum + tax.taxAmount, 0) || 0);

    // Check if taxes include VAT
    const hasVat = hasVatTax(document.taxes);
    const isInclusive = document.isInclusive || false;

    const renderQuotationContent = () => (
      <>
        <div className="mb-8 p-4 bg-gray-50 rounded">
          <p className="text-gray-600">
            This quotation is valid for 30 days from the date of issue. Please note that prices may
            vary based on market conditions and availability.
          </p>
        </div>
        {document.status && (
          <div className="absolute top-20 right-20">
            <div style={{
              backgroundColor: document.status === 'accepted' ? '#48bb78' : 
                              document.status === 'rejected' ? '#f56565' : 
                              '#ecc94b',
              color: 'white',
              borderRadius: '9999px',
              padding: '0.5rem 0.5rem', // Adjusted padding to fit the length
              display: 'inline-block' // Changed to inline-block to fit the length
            }}>
              {document.status.toUpperCase()}
            </div>
          </div>
        )}
      </>
    );

    console.log("document", document);

    const renderReceiptContent = () => (
      <>
        <div style={{ 
          marginBottom: '2.5rem', 
          padding: '1.5rem', 
          backgroundColor: '#ffffff', 
          borderRadius: '0.75rem',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
          border: '1px solid #f0f0f0'
        }}>
          <div style={{ marginBottom: '1.5rem', borderBottom: '1px solid #f0f0f0', paddingBottom: '1rem' }}>
            <h3 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '0.5rem' }}>
              Invoice #{document.invoiceNumber}
            </h3>
            <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0' }}>
              Transaction details for your records
            </p>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
            <div>
              <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.25rem' }}>Invoice Amount</p>
              <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>
                {currencySymbols[document.currency as Currency]}{document.invoiceAmount?.toFixed(2)}
              </p>
            </div>
            
            <div>
              <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.25rem' }}>Amount Paid</p>
              <p style={{ fontSize: '1.125rem', fontWeight: '600', color: '#111827' }}>
                {currencySymbols[document.currency as Currency]}{document.amount?.toFixed(2)}
              </p>
              <p style={{ fontSize: '0.75rem', color: '#6B7280' }}>via {document.paymentMethodName}</p>
            </div>
          </div>
          
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.25rem', marginBottom: '1.5rem' }}>
            <div>
              <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.25rem' }}>Payment Date</p>
              <p style={{ fontSize: '0.9375rem', color: '#111827' }}>{document.paymentDate}</p>
            </div>
            
            {document.paymentReference && (
              <div>
                <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.25rem' }}>Payment Reference</p>
                <p style={{ 
                  fontSize: '0.9375rem', 
                  color: '#111827', 
                  fontFamily: 'monospace', 
                  backgroundColor: '#F9FAFB', 
                  padding: '0.25rem 0.5rem', 
                  borderRadius: '0.25rem',
                  display: 'inline-block'
                }}>
                  {document.paymentReference}
                </p>
              </div>
            )}
          </div>
          
          {document.paymentAccount && (
            <div style={{ marginBottom: '1.5rem' }}>
              <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.25rem' }}>Payment Account</p>
              <p style={{ 
                fontSize: '0.9375rem', 
                color: '#111827', 
                fontFamily: 'monospace', 
                backgroundColor: '#F9FAFB', 
                padding: '0.25rem 0.5rem', 
                borderRadius: '0.25rem',
                display: 'inline-block'
              }}>
                {document.paymentAccount}
              </p>
            </div>
          )}
          
          <div style={{ 
            marginTop: '1.5rem', 
            paddingTop: '1.25rem', 
            borderTop: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <div>
              <p style={{ fontSize: '0.875rem', color: '#6B7280', marginBottom: '0.25rem' }}>Payment Status</p>
              {document.amount >= document.invoiceAmount ? 
                <div style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  padding: '0.375rem 0.75rem', 
                  borderRadius: '9999px', 
                  color: '#065F46', 
                  backgroundColor: '#ECFDF5',
                  border: '1px solid #D1FAE5',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  FULLY PAID
                </div>
                :
                <div style={{ 
                  display: 'inline-flex', 
                  alignItems: 'center',
                  padding: '0.375rem 0.75rem', 
                  borderRadius: '9999px', 
                  color: '#92400E', 
                  backgroundColor: '#FFFBEB',
                  border: '1px solid #FEF3C7',
                  fontSize: '0.75rem',
                  fontWeight: '600'
                }}>
                  PARTIALLY PAID
                </div>
              }
            </div>
            
            <div style={{ 
              width: '5rem', 
              height: '5rem', 
              display: 'flex', 
              justifyContent: 'center', 
              alignItems: 'center'
            }}>
              {document.amount >= document.invoiceAmount ? (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="24" fill="#ECFDF5"/>
                  <path d="M32 18L21 29L16 24" stroke="#10B981" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              ) : (
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="24" fill="#FFFBEB"/>
                  <path d="M24 16V24L28 28" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              )}
            </div>
          </div>
        </div>
      </>
    );

    // Add tax information display function
    const renderTaxInfo = () => {
      if (!document.businessTaxInfos || document.businessTaxInfos.length === 0) {
        return null;
      }
      
      return (
        <div style={{ 
          width: '100%',
          marginBottom: '30px',
          display: 'block',
          clear: 'both',
          overflow: 'hidden'
        }}>
          <div style={{ 
            float: 'right',
            width: '30%',
            padding: '15px', 
            border: '1px solid #e5e7eb', 
            borderRadius: '6px',
            backgroundColor: '#f9fafb'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 10px 0'
            }}>
              Business Tax Information
            </h3>
            <div>
              {document.businessTaxInfos.map((taxInfo: any, index: number) => (
                <div key={index} style={{ 
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: '12px',
                  marginBottom: '5px'
                }}>
                  <span style={{ fontWeight: '500', marginRight: '5px' }}>
                    {taxInfo.taxType.name}:
                  </span>
                  <span style={{ color: '#6b7280' }}>
                    {taxInfo.taxIdentifier}
                  </span>
                  {taxInfo.isVerified && (
                    <span style={{ 
                      marginLeft: '8px',
                      padding: '2px 6px',
                      borderRadius: '4px',
                      backgroundColor: '#d1fae5',
                      color: '#065f46',
                      fontSize: '10px',
                      fontWeight: '500'
                    }}>
                      Verified
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      );
    };

    // Add customer tax information display function
    const renderCustomerTaxInfo = () => {
      console.log("document.customerTaxInfos", document.customerTaxInfos);
      if (!document.customerTaxInfos || document.customerTaxInfos.length === 0) {
        return null;
      }
      
      return (
        <div style={{ 
          width: '100%',
          marginTop: '10px',
          display: 'block',
          clear: 'both'
        }}>
          <h3 style={{ 
            fontSize: '13px', 
            fontWeight: '600',
            color: '#374151',
            margin: '0 0 5px 0'
          }}>
            Customer Tax Information
          </h3>
          <div style={{ padding: '0' }}>
            {document.customerTaxInfos.map((taxInfo: any, index: number) => (
              <div key={index} style={{ 
                fontSize: '12px',
                marginBottom: '4px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <span style={{ fontWeight: '500', marginRight: '5px' }}>
                  {taxInfo.taxType?.name}:
                </span>
                <span style={{ color: '#6b7280' }}>
                  {taxInfo.taxIdentifier}
                </span>
                {taxInfo.isVerified && (
                  <span style={{ 
                    marginLeft: '8px',
                    padding: '1px 4px',
                    borderRadius: '4px',
                    backgroundColor: '#d1fae5',
                    color: '#065f46',
                    fontSize: '9px',
                    fontWeight: '500'
                  }}>
                    Verified
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      );
    };

    return (
      <div ref={componentRef} style={{ 
        backgroundColor: '#ffffff', 
        position: 'relative',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
        fontSize: '14px',
        lineHeight: '1.5',
        color: '#1f2937',
        maxWidth: '1000px',
        margin: '0 auto',
        padding: '40px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'flex-start',
          marginBottom: '40px'
        }}>
          <div>
            <h2 style={{ 
              fontSize: '20px', 
              fontWeight: '600', 
              color: '#111827',
              margin: '0 0 4px 0'
            }}>
              {document.type.toUpperCase()} #{document.number}
            </h2>
            {document.type === 'receipt' && document.invoiceNumber && (
              <p style={{ 
                fontSize: '14px', 
                color: '#6b7280',
                margin: '0'
              }}>
                For Invoice #{document.invoiceNumber}
              </p>
            )}
          </div>
          <div style={{ textAlign: 'right' }}>
            {document.logo && (
              <div style={{ marginBottom: '16px' }}>
                <img
                  src={document.logo}
                  alt="Company Logo"
                  style={{ 
                    maxWidth: '120px', 
                    maxHeight: '60px', 
                    objectFit: 'contain' 
                  }}
                />
              </div>
            )}
            <p style={{ margin: '0', fontWeight: '500' }}>{document.companyDetails.name}</p>
            <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>{document.companyDetails.address}</p>
            <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>{document.companyDetails.emailAddress}</p>
            <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>{document.companyDetails.phoneNumber}</p>
          </div>
        </div>
        
        {(document.type === 'invoice' || document.type === 'quotation') && renderTaxInfo()}

        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '30px'
        }}>
          <div style={{ maxWidth: '50%' }}>
            <h3 style={{ 
              fontSize: '15px', 
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 12px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              {document.type === 'receipt' ? 'Paid For:' : document.type === 'bill' ? 'Pay To:' : 'Bill To:'}
            </h3>
            {document.type === 'bill' && document.supplierDetails ? (
              <div>
                <p style={{ margin: '0', fontWeight: '500' }}>{document.supplierDetails.companyName || document.supplierDetails.name}</p>
                {document.supplierDetails.name !== document.supplierDetails.companyName && document.supplierDetails.name && (
                  <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>Contact: {document.supplierDetails.name}</p>
                )}
                <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>{document.supplierDetails.address}</p>
                <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>{document.supplierDetails.emailAddress}</p>
                <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>{document.supplierDetails.phoneNumber}</p>
              </div>
            ) : document.type !== 'bill' && document.customerDetails ? (
              <div>
                {document.type !== 'receipt' && (
                  <>
                    <p style={{ margin: '0', fontWeight: '500' }}>{document.customerDetails.companyName || document.customerDetails.name}</p>
                    {document.customerDetails.name !== document.customerDetails.companyName && document.customerDetails.name && (
                      <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>Contact: {document.customerDetails.name}</p>
                    )}
                    <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>{document.customerDetails.address}</p>
                    <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>{document.customerDetails.emailAddress}</p>
                    <p style={{ margin: '2px 0', color: '#6b7280', fontSize: '13px' }}>{document.customerDetails.phoneNumber}</p>
                    {renderCustomerTaxInfo()}
                  </>
                )}
              </div>
            ) : null}
          </div>
          
          <div style={{ textAlign: 'right' }}>
            <h3 style={{ 
              fontSize: '15px', 
              fontWeight: '600',
              color: '#374151',
              margin: '0 0 12px 0',
              textTransform: 'uppercase',
              letterSpacing: '0.05em'
            }}>
              Details
            </h3>
            <div style={{ fontSize: '13px' }}>
              {document.dueDate && (
                <div style={{ margin: '4px 0', display: 'flex', justifyContent: 'flex-end' }}>
                  <span style={{ color: '#6b7280', marginRight: '8px' }}>Due Date:</span>
                  <span style={{ fontWeight: '500' }}>{document.dueDate}</span>
                </div>
              )}
              {document.issueDate && (
                <div style={{ margin: '4px 0', display: 'flex', justifyContent: 'flex-end' }}>
                  <span style={{ color: '#6b7280', marginRight: '8px' }}>Issue Date:</span>
                  <span style={{ fontWeight: '500' }}>{document.issueDate}</span>
                </div>
              )}
              {document.expiryDate && (
                <div style={{ margin: '4px 0', display: 'flex', justifyContent: 'flex-end' }}>
                  <span style={{ color: '#6b7280', marginRight: '8px' }}>Expiry Date:</span>
                  <span style={{ fontWeight: '500' }}>{document.expiryDate}</span>
                </div>
              )}
              {document.type === 'receipt' && document.paymentDate && (
                <div style={{ margin: '4px 0', display: 'flex', justifyContent: 'flex-end' }}>
                  <span style={{ color: '#6b7280', marginRight: '8px' }}>Payment Date:</span>
                  <span style={{ fontWeight: '500' }}>{document.paymentDate}</span>
                </div>
              )}
              {document.type === 'receipt' && document.paymentMethodName && (
                <div style={{ margin: '4px 0', display: 'flex', justifyContent: 'flex-end' }}>
                  <span style={{ color: '#6b7280', marginRight: '8px' }}>Payment Method:</span>
                  <span style={{ fontWeight: '500' }}>{document.paymentMethodName}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {document.type === 'quotation' && renderQuotationContent()}
        {document.type === 'receipt' && renderReceiptContent()}
      
        {document.type !== 'receipt' && (
          <div style={{ marginBottom: '30px' }}>
            <table style={{ 
              width: '100%', 
              borderCollapse: 'collapse',
              fontSize: '13px'
            }}>
              <thead>
                <tr style={{ 
                  backgroundColor: '#f9fafb', 
                  borderBottom: '1px solid #e5e7eb'
                }}>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '12px 16px', 
                    fontWeight: '600',
                    color: '#374151'
                  }}>Name</th>
                  <th style={{ 
                    textAlign: 'left', 
                    padding: '12px 16px', 
                    fontWeight: '600',
                    color: '#374151'
                  }}>Description</th>
                  <th style={{ 
                    textAlign: 'right', 
                    padding: '12px 16px', 
                    fontWeight: '600',
                    color: '#374151'
                  }}>Quantity</th>
                  <th style={{ 
                    textAlign: 'right', 
                    padding: '12px 16px', 
                    fontWeight: '600',
                    color: '#374151'
                  }}>Price</th>
                  {hasVat ? (
                    <>
                      <th style={{ 
                        textAlign: 'right', 
                        padding: '12px 16px', 
                        fontWeight: '600',
                        color: '#374151'
                      }}>Total Excl. VAT</th>
                      <th style={{ 
                        textAlign: 'right', 
                        padding: '12px 16px', 
                        fontWeight: '600',
                        color: '#374151'
                      }}>VAT Amount</th>
                      <th style={{ 
                        textAlign: 'right', 
                        padding: '12px 16px', 
                        fontWeight: '600',
                        color: '#374151'
                      }}>Total Incl. VAT</th>
                    </>
                  ) : (
                    <th style={{ 
                      textAlign: 'right', 
                      padding: '12px 16px', 
                      fontWeight: '600',
                      color: '#374151'
                    }}>Amount</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {document.items.map((item: any, index: any) => {
                  const vatCalc = hasVat ? calculateItemVat(item, document.taxes, isInclusive) : null;
                  return (
                    <tr key={index} style={{ 
                      borderBottom: '1px solid #e5e7eb'
                    }}>
                      {item.name && <td style={{ padding: '12px 16px' }}>{item.name}</td>}
                      <td style={{ padding: '12px 16px' }}>{item.description}</td>
                      <td style={{ textAlign: 'right', padding: '12px 16px' }}>{item.quantity}</td>
                      <td style={{ textAlign: 'right', padding: '12px 16px' }}>
                        {currencySymbols[document.currency as Currency]}{item.unitPrice && item.unitPrice.toFixed(2)}
                      </td>
                      {hasVat ? (
                        <>
                          <td style={{ textAlign: 'right', padding: '12px 16px' }}>
                            {currencySymbols[document.currency as Currency]}{vatCalc?.totalExclVat?.toFixed(2) || '0.00'}
                          </td>
                          <td style={{ textAlign: 'right', padding: '12px 16px' }}>
                            {currencySymbols[document.currency as Currency]}{vatCalc?.vatAmount?.toFixed(2) || '0.00'}
                          </td>
                          <td style={{ textAlign: 'right', padding: '12px 16px' }}>
                            {currencySymbols[document.currency as Currency]}{vatCalc?.totalInclVat?.toFixed(2) || '0.00'}
                          </td>
                        </>
                      ) : (
                        <td style={{ textAlign: 'right', padding: '12px 16px' }}>
                          {currencySymbols[document.currency as Currency]}{(item.quantity * item.unitPrice).toFixed(2)}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={hasVat ? 6 : 4} style={{ 
                    textAlign: 'right', 
                    padding: '12px 16px', 
                    fontWeight: '500',
                    color: '#4b5563'
                  }}>
                    Subtotal:
                  </td>
                  <td style={{ 
                    textAlign: 'right', 
                    padding: '12px 16px', 
                    fontWeight: '600'
                  }}>
                    {currencySymbols[document.currency as Currency]}{document.items.reduce((sum: number, item: any) => sum + item.quantity * item.unitPrice, 0).toFixed(2)}
                  </td>
                </tr>
                {document.taxes && document.taxes.map((tax: any, index: number) => (
                  <tr key={`tax-${index}`}>
                    <td colSpan={hasVat ? 6 : 4} style={{ 
                      textAlign: 'right', 
                      padding: '8px 16px', 
                      fontWeight: '500',
                      color: '#4b5563'
                    }}>
                      {tax.taxName} ({tax.taxRate}%):
                    </td>
                    <td style={{ 
                      textAlign: 'right', 
                      padding: '8px 16px', 
                      fontWeight: '600'
                    }}>
                      {currencySymbols[document.currency as Currency]}{tax.taxAmount.toFixed(2)}
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={hasVat ? 6 : 4} style={{ 
                    textAlign: 'right', 
                    padding: '16px', 
                    fontWeight: '600',
                    fontSize: '15px',
                    borderTop: '2px solid #e5e7eb'
                  }}>
                    Total:
                  </td>
                  <td style={{ 
                    textAlign: 'right', 
                    padding: '16px', 
                    fontWeight: '700',
                    fontSize: '15px',
                    borderTop: '2px solid #e5e7eb'
                  }}>
                    {currencySymbols[document.currency as Currency]}{total.toFixed(2)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}

        {document.notes && (
          <div style={{ 
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px',
            fontSize: '13px'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: '600',
              margin: '0 0 8px 0',
              color: '#374151'
            }}>Notes</h3>
            <p style={{ margin: '0', color: '#4b5563' }}>{document.notes}</p>
          </div>
        )}

        {document.terms && (
          <div style={{ 
            marginBottom: '24px',
            padding: '16px',
            backgroundColor: '#f9fafb',
            borderRadius: '6px',
            fontSize: '13px'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: '600',
              margin: '0 0 8px 0',
              color: '#374151'
            }}>Terms and Conditions</h3>
            <p style={{ margin: '0', color: '#4b5563' }}>{document.terms}</p>
          </div>
        )}

        {document.signature && (
          <div style={{ 
            marginTop: '40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start'
          }}>
            <img
              src={document.signature}
              alt="Signature"
              style={{ 
                maxHeight: '40px',
                marginBottom: '8px'
              }}
            />
            <div style={{ 
              borderTop: '1px solid #e5e7eb', 
              width: '200px',
              paddingTop: '4px'
            }}>
              <p style={{ 
                fontSize: '12px', 
                color: '#6b7280',
                margin: '0'
              }}>Authorized Signature</p>
            </div>
          </div>
        )}

        <div style={{ 
          marginTop: '60px',
          textAlign: 'center',
          borderTop: '1px solid #e5e7eb',
          paddingTop: '20px',
          fontSize: '12px',
          color: '#9ca3af'
        }}>
          <p style={{ margin: '0 0 4px 0' }}>Thank you for your business!</p>
          {brand.name !== 'Thaton Realty' && (
            <p style={{ margin: '0' }}>
              Powered by <a href={`https://www.${brand.domain}`} target="_blank" rel="noopener noreferrer" style={{ 
                color: '#6366f1',
                textDecoration: 'none',
                fontWeight: '500'
              }}>{brand.name}</a>
            </p>
          )}
        </div>
      </div>
    );
  }
);

export default PrintableDocument;